import { useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, Plus, Calendar, AlertTriangle, CheckCircle, Clock, X,
  Shield, Globe, Key, FileText, RefreshCw, MoreVertical, Edit, Trash2,
  ChevronDown, ExternalLink, Bell
} from 'lucide-react';
import { documentationAPI, adminCompanyAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import { isInternalRole } from '../../utils/roles.js';

// Type icons mapping
const TYPE_ICONS = {
  ssl_certificate: Shield,
  domain: Globe,
  license: Key,
  warranty: Calendar,
  contract: FileText,
  subscription: RefreshCw,
  other: Calendar
};

const TYPE_LABELS = {
  ssl_certificate: 'SSL Certificate',
  domain: 'Domain',
  license: 'License',
  warranty: 'Warranty',
  contract: 'Contract',
  subscription: 'Subscription',
  other: 'Other'
};

// Urgency badge
function UrgencyBadge({ urgency, daysUntil }) {
  const config = {
    expired: { icon: AlertTriangle, label: 'Expired', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    critical: { icon: AlertTriangle, label: `${daysUntil}d`, className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    warning: { icon: Clock, label: `${daysUntil}d`, className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
    ok: { icon: CheckCircle, label: `${daysUntil}d`, className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[urgency] || config.ok;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// Type badge
function TypeBadge({ type }) {
  const Icon = TYPE_ICONS[type] || TYPE_ICONS.other;
  const label = TYPE_LABELS[type] || type;

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300">
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// Status badge
function StatusBadge({ status }) {
  const config = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    expired: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    renewed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    cancelled: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${config[status] || config.active}`}>
      {status}
    </span>
  );
}

// Summary cards
function SummaryCards({ summary }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center justify-between">
          <span className="text-sm text-neutral-600 dark:text-neutral-400">Total Active</span>
          <Calendar className="w-5 h-5 text-neutral-400" />
        </div>
        <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">{summary.totalActive || 0}</p>
      </div>
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-700 dark:text-red-400">Expiring This Week</span>
          <AlertTriangle className="w-5 h-5 text-red-500" />
        </div>
        <p className="text-2xl font-bold text-red-700 dark:text-red-400 mt-1">{summary.expiringWeek || 0}</p>
      </div>
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
        <div className="flex items-center justify-between">
          <span className="text-sm text-yellow-700 dark:text-yellow-400">Expiring This Month</span>
          <Clock className="w-5 h-5 text-yellow-500" />
        </div>
        <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400 mt-1">{summary.expiringMonth || 0}</p>
      </div>
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between">
          <span className="text-sm text-blue-700 dark:text-blue-400">Expiring in 90 Days</span>
          <Bell className="w-5 h-5 text-blue-500" />
        </div>
        <p className="text-2xl font-bold text-blue-700 dark:text-blue-400 mt-1">{summary.expiringQuarter || 0}</p>
      </div>
    </div>
  );
}

// Expiration modal
function ExpirationModal({ expiration, companyId, isInternal, onClose }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: expiration?.name || '',
    description: expiration?.description || '',
    expirationType: expiration?.expiration_type || 'other',
    expiresAt: expiration?.expires_at?.split('T')[0] || '',
    autoRenew: expiration?.auto_renew || false,
    renewalCost: expiration?.renewal_cost || '',
    renewalVendor: expiration?.renewal_vendor || '',
    renewalUrl: expiration?.renewal_url || '',
    renewalContactName: expiration?.renewal_contact_name || '',
    renewalContactEmail: expiration?.renewal_contact_email || '',
    visibility: expiration?.visibility || 'private',
    companyId: expiration?.company_id || companyId || '',
  });
  const [error, setError] = useState(null);

  // Fetch companies for internal staff
  const { data: companiesData } = useQuery({
    queryKey: ['admin', 'companies', 'list'],
    queryFn: () => adminCompanyAPI.list({ limit: 500 }),
    enabled: isInternal,
  });
  const companies = companiesData?.data?.companies || [];

  const createMutation = useMutation({
    mutationFn: (data) => documentationAPI.createExpiration({ ...data, companyId: data.companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'expirations'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to create expiration'),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => documentationAPI.updateExpiration(expiration.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'expirations'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to update expiration'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.expiresAt) {
      setError('Name and expiry date are required');
      return;
    }
    if (!expiration && !formData.companyId) {
      setError('Company is required');
      return;
    }
    if (expiration) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 bg-white dark:bg-neutral-800">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {expiration ? 'Edit Expiration' : 'Track New Expiration'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Company selector for internal staff */}
          {isInternal && !expiration && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Company *</label>
              <select
                value={formData.companyId}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value ? parseInt(e.target.value) : '' })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                required
              >
                <option value="">Select a company...</option>
                {companies.map(company => (
                  <option key={company.id} value={company.cw_company_id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
              placeholder="e.g., example.com SSL Certificate"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Type *</label>
              <select
                value={formData.expirationType}
                onChange={(e) => setFormData({ ...formData, expirationType: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
              >
                <option value="ssl_certificate">SSL Certificate</option>
                <option value="domain">Domain</option>
                <option value="license">License</option>
                <option value="warranty">Warranty</option>
                <option value="contract">Contract</option>
                <option value="subscription">Subscription</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Expires On *</label>
              <input
                type="date"
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 resize-none"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoRenew"
              checked={formData.autoRenew}
              onChange={(e) => setFormData({ ...formData, autoRenew: e.target.checked })}
              className="w-4 h-4 text-brand-600 border-neutral-300 rounded focus:ring-brand-500"
            />
            <label htmlFor="autoRenew" className="text-sm text-neutral-700 dark:text-neutral-300">
              Auto-renews
            </label>
          </div>

          {/* Renewal Info */}
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Renewal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Vendor</label>
                <input
                  type="text"
                  value={formData.renewalVendor}
                  onChange={(e) => setFormData({ ...formData, renewalVendor: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  placeholder="e.g., GoDaddy"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Cost</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.renewalCost}
                  onChange={(e) => setFormData({ ...formData, renewalCost: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Renewal URL</label>
              <input
                type="url"
                value={formData.renewalUrl}
                onChange={(e) => setFormData({ ...formData, renewalUrl: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Contact Name</label>
                <input
                  type="text"
                  value={formData.renewalContactName}
                  onChange={(e) => setFormData({ ...formData, renewalContactName: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={formData.renewalContactEmail}
                  onChange={(e) => setFormData({ ...formData, renewalContactEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white rounded-lg"
            >
              {isLoading ? 'Saving...' : expiration ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Renew modal
function RenewModal({ expiration, onClose }) {
  const queryClient = useQueryClient();
  const [newExpiresAt, setNewExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);

  const renewMutation = useMutation({
    mutationFn: () => documentationAPI.renewExpiration(expiration.id, { newExpiresAt, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'expirations'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to renew'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newExpiresAt) {
      setError('New expiry date is required');
      return;
    }
    renewMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Renew "{expiration.name}"</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">New Expiry Date *</label>
            <input
              type="date"
              value={newExpiresAt}
              onChange={(e) => setNewExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional renewal notes..."
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 resize-none"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={renewMutation.isPending}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg"
            >
              {renewMutation.isPending ? 'Renewing...' : 'Renew'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Expiration row
function ExpirationRow({ expiration, isInternal, onEdit, onRenew, onDelete }) {
  const [showMenu, setShowMenu] = useState(false);
  const Icon = TYPE_ICONS[expiration.expiration_type] || TYPE_ICONS.other;

  return (
    <tr className="group hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-neutral-100 dark:bg-neutral-700 rounded-lg">
            <Icon className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
          </div>
          <div>
            <p className="font-medium text-neutral-900 dark:text-white">{expiration.name}</p>
            {expiration.description && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate max-w-xs">{expiration.description}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <TypeBadge type={expiration.expiration_type} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-900 dark:text-white">
            {new Date(expiration.expires_at).toLocaleDateString()}
          </span>
          <UrgencyBadge urgency={expiration.urgency} daysUntil={expiration.days_until_expiry} />
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={expiration.status} />
        {expiration.auto_renew && (
          <span className="ml-2 text-xs text-green-600 dark:text-green-400">Auto-renews</span>
        )}
      </td>
      <td className="px-4 py-3">
        {expiration.renewal_vendor && (
          <span className="text-sm text-neutral-600 dark:text-neutral-400">{expiration.renewal_vendor}</span>
        )}
        {expiration.renewal_url && (
          <a
            href={expiration.renewal_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 text-brand-600 hover:text-brand-700"
          >
            <ExternalLink className="w-3 h-3 inline" />
          </a>
        )}
      </td>
      {isInternal && (
        <td className="px-4 py-3 text-right">
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 opacity-0 group-hover:opacity-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-opacity"
            >
              <MoreVertical className="w-4 h-4 text-neutral-500" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-1 min-w-[120px]">
                  <button
                    onClick={() => { setShowMenu(false); onRenew(expiration); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Renew
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); onEdit(expiration); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); onDelete(expiration); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

// Delete confirmation
function DeleteConfirmModal({ expiration, onClose }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: () => documentationAPI.deleteExpiration(expiration.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'expirations'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to delete'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Delete Expiration?</h2>
        <p className="text-neutral-600 dark:text-neutral-400 mb-4">
          Are you sure you want to delete "{expiration.name}"?
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300">
            Cancel
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExpirationsPage() {
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [expirationToEdit, setExpirationToEdit] = useState(null);
  const [expirationToRenew, setExpirationToRenew] = useState(null);
  const [expirationToDelete, setExpirationToDelete] = useState(null);
  const { user } = useAuthStore();

  const isInternal = isInternalRole(user?.role);
  const companyId = user?.cw_company_id;

  // Fetch summary
  const { data: summaryData } = useQuery({
    queryKey: ['documentation', 'expirations', 'summary', companyId],
    queryFn: () => documentationAPI.getExpirationSummary({ companyId: isInternal ? undefined : companyId }),
  });

  // Fetch expirations
  const debouncedSearch = useDebounce(search, 300);
  const { data: expirationsData, isLoading } = useQuery({
    queryKey: ['documentation', 'expirations', { search: debouncedSearch, type: selectedType, companyId: isInternal ? undefined : companyId }],
    queryFn: () => documentationAPI.listExpirations({
      search: debouncedSearch || undefined,
      type: selectedType || undefined,
      companyId: isInternal ? undefined : companyId,
      limit: 50
    }),
  });

  const summary = summaryData?.data?.summary || {};
  const expirations = expirationsData?.data?.expirations || [];

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Expirations</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Track SSL certificates, domains, licenses, warranties, and more
          </p>
        </div>
        {isInternal && (
          <button
            onClick={() => { setExpirationToEdit(null); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Track Expiration
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <SummaryCards summary={summary} />

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search expirations..."
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <div className="relative">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="appearance-none pl-4 pr-10 py-2.5 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
          >
            <option value="">All Types</option>
            <option value="ssl_certificate">SSL Certificate</option>
            <option value="domain">Domain</option>
            <option value="license">License</option>
            <option value="warranty">Warranty</option>
            <option value="contract">Contract</option>
            <option value="subscription">Subscription</option>
            <option value="other">Other</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
        </div>
      </div>

      {/* Expirations Table */}
      {isLoading ? (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-8">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-neutral-200 dark:bg-neutral-700 rounded" />
            ))}
          </div>
        </div>
      ) : expirations.length > 0 ? (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Vendor</th>
                {isInternal && <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {expirations.map(exp => (
                <ExpirationRow
                  key={exp.id}
                  expiration={exp}
                  isInternal={isInternal}
                  onEdit={(e) => { setExpirationToEdit(e); setShowModal(true); }}
                  onRenew={setExpirationToRenew}
                  onDelete={setExpirationToDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <Calendar className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">No expirations tracked</h3>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            {isInternal ? 'Start tracking SSL certificates, domains, and other important dates' : 'No expirations to display'}
          </p>
          {isInternal && (
            <button
              onClick={() => { setExpirationToEdit(null); setShowModal(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Track Expiration
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ExpirationModal
          expiration={expirationToEdit}
          companyId={companyId}
          isInternal={isInternal}
          onClose={() => { setShowModal(false); setExpirationToEdit(null); }}
        />
      )}

      {expirationToRenew && (
        <RenewModal
          expiration={expirationToRenew}
          onClose={() => setExpirationToRenew(null)}
        />
      )}

      {expirationToDelete && (
        <DeleteConfirmModal
          expiration={expirationToDelete}
          onClose={() => setExpirationToDelete(null)}
        />
      )}
    </div>
  );
}
